import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "../../components/ui/table";
import { Button } from "../../components/ui/button";
import {
  Sheet,
  SheetContent,
} from "../../components/ui/sheet";
import { DownloadCloud, Loader2, User } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "../../components/ui/avatar";
import { Tabs, TabsContent } from "../../components/ui/tabs";
import { Badge } from "../../components/ui/badge";
import { Skeleton } from "../../components/ui/skeleton";
import { FetchCibilDialog } from "../../components/bureau/FetchCibilDialog";
import {
  DASH,
  formatCompactINR,
  formatDate as formatCibilDate,
  formatNumber,
  NO_HISTORY_SCORE,
} from "../../lib/cibil/format";
import {
  getDirectorsCibil,
  type DirectorCibilRow,
} from "../../services/cibilApi";
import { cn } from "../../lib/utils";
import ViewCibilReport from "./ViewCibilReport";
import { DirectorApolloPanel } from "../../components/director-overview";

const SCORE_MIN = 300;
const SCORE_MAX = 900;

function resolveApolloPhotoUrl(photoUrl?: string | null): string | undefined {
  const trimmed = photoUrl?.trim();
  if (!trimmed) return undefined;
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  const base = import.meta.env.VITE_API_BASE_URL?.replace(/\/$/, "") ?? "";
  return base ? `${base}/${trimmed.replace(/^\//, "")}` : trimmed;
}

function DirectorRowAvatar({
  name,
  photoUrl,
}: {
  name: string;
  photoUrl?: string | null;
}) {
  const src = resolveApolloPhotoUrl(photoUrl);
  return (
    <Avatar className="h-8 w-8 shrink-0">
      {src ? <AvatarImage src={src} alt={name || "Director"} /> : null}
      <AvatarFallback className="bg-gray-100 text-gray-500">
        <User className="h-4 w-4" />
      </AvatarFallback>
    </Avatar>
  );
}

/** Compact horizontal bureau score for the directors table. */
function BureauScoreBar({
  score,
  onClick,
  disabled,
}: {
  score: number | null;
  onClick?: () => void;
  disabled?: boolean;
}) {
  const noHistory = score === NO_HISTORY_SCORE;
  const pct =
    score == null || noHistory
      ? 0
      : Math.min(1, Math.max(0, (score - SCORE_MIN) / (SCORE_MAX - SCORE_MIN)));

  return (
    <button
      type="button"
      disabled={disabled || !onClick}
      onClick={onClick}
      title={onClick ? "View CIBIL report" : undefined}
      className={cn(
        "inline-flex w-[7.5rem] flex-col items-center gap-1",
        onClick && !disabled && "cursor-pointer",
        disabled && "opacity-60",
      )}
    >
      <span className="text-sm font-semibold tabular-nums leading-none text-gray-900">
        {score == null ? DASH : noHistory ? "NH" : formatNumber(score)}
      </span>
      <span className="h-1.5 w-full overflow-hidden rounded-full bg-gray-200">
        <span
          className="block h-full rounded-full bg-gradient-to-r from-red-500 via-amber-400 to-green-500 transition-[width] duration-500"
          style={{ width: `${pct * 100}%` }}
        />
      </span>
    </button>
  );
}

// Define types for our data
type Address = {
  address_line1: string | null;
  address_line2: string | null;
  city: string | null;
  state: string | null;
  pincode: string | null;
  country: string | null;
};

type Director = {
  pan: string;
  din: string;
  name: string;
  designation: string;
  din_status: string;
  gender: string;
  date_of_birth: string;
  age: number;
  date_of_appointment: string;
  date_of_appointment_for_current_designation: string;
  date_of_cessation: string | null;
  nationality: string;
  dsc_status: string | null;
  dsc_expiry_date: string | null;
  father_name: string | null;
  address: Address;
  director_uuid?: string;
  mobile_number?: string;
  email?: string;
};

type CompanyNetwork = {
  cin: string;
  legal_name: string;
  company_status: string;
  incorporation_date: string;
  paid_up_capital: number;
  sum_of_charges: number;
  city: string;
  active_compliance: string | null;
  cirp_status: string | null;
  designation: string;
  date_of_appointment: string;
  date_of_appointment_for_current_designation: string;
  date_of_cessation: string | null;
};

type DirectorNetwork = {
  name: string;
  pan: string;
  din: string;
  network: {
    companies: CompanyNetwork[];
    llps: any[];
  };
};

type DirectorData = {
  director: Director[] | [],
  directorNetworks: DirectorNetwork[] | [],
  customerId?: string;
};

const DirectorDetails: React.FC<DirectorData> = ({
  director, directorNetworks, customerId,
}) => {
  const [selectedDirector, setSelectedDirector] = useState<Director | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<string>("directors");
  const [activeTab2, setActiveTab2] = useState<string>("present");
  const [presentDirectorships, setPresentDirectorships] = useState<CompanyNetwork[]>([]);;
  const [pastDirectorships, setPastDirectorships] = useState<CompanyNetwork[]>([]);
  const [cibilRows, setCibilRows] = useState<DirectorCibilRow[]>([]);
  const [cibilLoading, setCibilLoading] = useState(false);
  const [viewing, setViewing] = useState<DirectorCibilRow | null>(null);
  const [fetchFor, setFetchFor] = useState<DirectorCibilRow | null>(null);

  const ceasedDirectorsList = director.filter(d => d.date_of_cessation !== null);

  const directorByPan = useMemo(() => {
    const map = new Map<string, Director>();
    for (const d of director) {
      const pan = d.pan?.trim().toUpperCase();
      if (pan) map.set(pan, d);
    }
    return map;
  }, [director]);

  const directorByUuid = useMemo(() => {
    const map = new Map<string, Director>();
    for (const d of director) {
      const uuid = d.director_uuid?.trim();
      if (uuid) map.set(uuid, d);
    }
    return map;
  }, [director]);

  const normalizePersonName = (name?: string | null) =>
    name?.trim().replace(/\s+/g, " ").toUpperCase() || "";

  const loadCibil = useCallback(() => {
    if (!customerId) {
      setCibilRows([]);
      setCibilLoading(false);
      return;
    }
    setCibilLoading(true);
    getDirectorsCibil(customerId)
      .then(setCibilRows)
      .catch(() => setCibilRows([]))
      .finally(() => setCibilLoading(false));
  }, [customerId]);

  useEffect(() => {
    loadCibil();
  }, [loadCibil]);

  const handleViewDetails = (director: Director) => {
    console.log("handleViewDetails_director", director);
    setSelectedDirector(director);
    setIsDrawerOpen(true);
  };
  const getDirectorNetworkByDin = (din: string) => {
    return directorNetworks.find(network => network.din === din);
  };

  useEffect(() => {
    if (directorNetworks !== undefined) {
      const directorNetwork = selectedDirector
        ? getDirectorNetworkByDin(selectedDirector.din)
        : null;

      setPresentDirectorships(
        directorNetwork?.network.companies.filter(
          (company) => company.date_of_cessation === null
        ) || []
      );

      setPastDirectorships(
        directorNetwork?.network.companies.filter(
          (company) => company.date_of_cessation !== null
        ) || []
      );
    }
  }, [selectedDirector]);

  const handleTabChange = (tab: string) => {
    setActiveTab2(tab);
  };

  const renderCibilRow = (cibil: DirectorCibilRow) => {
    const fetched = cibil.message === null;
    const cibilPan = cibil.pan?.trim().toUpperCase() || "";
    const cibilUuid = cibil.director_uuid?.trim() || "";
    const cibilName = normalizePersonName(cibil.full_name);

    // PAN can be null on CIBIL and/or director — fall back to uuid, then name.
    const matchedBase =
      (cibilPan ? directorByPan.get(cibilPan) : undefined) ??
      (cibilUuid ? directorByUuid.get(cibilUuid) : undefined) ??
      (cibilName
        ? director.find((d) => normalizePersonName(d.name) === cibilName)
        : undefined);

    const matched = matchedBase
      ? {
          ...matchedBase,
          director_uuid:
            matchedBase.director_uuid?.trim() ||
            cibilUuid ||
            matchedBase.director_uuid,
        }
      : undefined;

    const editDirector: Director = matched ?? {
      pan: cibil.pan || "",
      din: "",
      name: cibil.full_name || "",
      designation: "",
      din_status: "",
      gender: "",
      date_of_birth: "",
      age: 0,
      date_of_appointment: "",
      date_of_appointment_for_current_designation: "",
      date_of_cessation: null,
      nationality: "",
      dsc_status: null,
      dsc_expiry_date: null,
      father_name: null,
      address: {
        address_line1: null,
        address_line2: null,
        city: null,
        state: null,
        pincode: null,
        country: null,
      },
      director_uuid: cibilUuid,
    };

    const designation =
      cibil.designation_name?.trim() ||
      matched?.designation?.trim() ||
      "";

    return (
      <TableRow
        key={cibil.director_uuid || cibil.pan || cibil.full_name}
        className="hover:bg-gray-50 transition-colors"
      >
        <TableCell className="w-[220px] min-w-[200px] border-r border-gray-200 py-2.5 pr-4">
          <button
            type="button"
            className="flex w-full items-center gap-2.5 text-left"
            onClick={() => handleViewDetails(editDirector)}
          >
            <DirectorRowAvatar
              name={cibil.full_name || ""}
              photoUrl={cibil.apollo_photo_url}
            />
            <div className="min-w-0">
              <p
                className="truncate text-sm font-medium text-gray-900 hover:text-blue-600"
                title={cibil.full_name || undefined}
              >
                {cibil.full_name || DASH}
              </p>
              <p
                className="truncate text-xs text-gray-500"
                title={designation || undefined}
              >
                {designation || DASH}
              </p>
            </div>
          </button>
        </TableCell>
        <TableCell className="px-3 py-2.5 text-center tabular-nums">
          {fetched ? (
            <Badge variant={(cibil.dpd_accounts ?? 0) > 0 ? "destructive" : "success"}>
              {formatNumber(cibil.dpd_accounts)}
            </Badge>
          ) : (
            DASH
          )}
        </TableCell>
        <TableCell className="px-3 py-2.5 text-center tabular-nums">
          {fetched ? (
            <span className={(cibil.overdue ?? 0) > 0 ? "text-red-600" : undefined}>
              {formatCompactINR(cibil.overdue)}
            </span>
          ) : (
            DASH
          )}
        </TableCell>
        <TableCell className="px-3 py-2.5 text-center">
          {fetched ? (
            <Badge variant={(cibil.enquires ?? 0) > 0 ? "warning" : "success"}>
              {formatNumber(cibil.enquires)}
            </Badge>
          ) : (
            DASH
          )}
        </TableCell>
        <TableCell className="px-3 py-2.5 text-center tabular-nums">
          {fetched
            ? cibil.max_dpd === null
              ? DASH
              : formatNumber(cibil.max_dpd)
            : DASH}
        </TableCell>
        <TableCell className="px-3 py-2.5 text-center whitespace-nowrap text-xs text-gray-600">
          {fetched && cibil.created_at
            ? formatCibilDate(new Date(cibil.created_at))
            : DASH}
        </TableCell>
        <TableCell className="px-3 py-2.5 text-center">
          <div className="flex items-center justify-center">
            {fetched ? (
              <BureauScoreBar
                score={cibil.cibil_score}
                disabled={cibil.id == null}
                onClick={
                  cibil.id != null ? () => setViewing(cibil) : undefined
                }
              />
            ) : (
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-7 w-7 text-blue-600"
                title="Fetch CIBIL"
                onClick={() => setFetchFor(cibil)}
              >
                <DownloadCloud className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>
        </TableCell>
      </TableRow>
    );
  };

  const renderDirectorRow = (d: Director) => {
    const dinValue = d.din?.trim() ? d.din : "null";
    const dinLabel = `din: ${dinValue}`;
    return (
      <TableRow
        key={d.din ?? Math.random().toString(36).substring(2, 9)}
        className="hover:bg-gray-50 transition-colors"
      >
        <TableCell className="w-[36%] py-2.5 pr-4">
          <button
            type="button"
            className="block w-full min-w-0 text-left"
            onClick={() => handleViewDetails(d)}
          >
            <p
              className="truncate text-sm font-medium text-gray-900 hover:text-blue-600"
              title={d.name || undefined}
            >
              {d.name}
            </p>
            <p className="truncate text-xs text-gray-500" title={dinLabel}>
              {dinLabel}
            </p>
          </button>
        </TableCell>
        <TableCell className="w-[44%] px-4 py-2.5 text-sm text-gray-900">
          <span className="block truncate" title={d.designation || undefined}>
            {d.designation || DASH}
          </span>
        </TableCell>
        <TableCell className="w-[20%] px-4 py-2.5 text-sm tabular-nums text-gray-900">
          {d.age != null && d.age > 0 ? d.age : DASH}
        </TableCell>
      </TableRow>
    );
  };

  if (viewing) {
    return (
      <ViewCibilReport director={viewing} onBack={() => setViewing(null)} />
    );
  }

  return (
    <div className="w-full">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold">Management Profile</h1>
        </div>
      </div>

      {/* Tabs for Directors, Shareholding, Related Party, and Securities Allotment */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full mb-6">

        <div className="mb-6">
          <nav className="flex space-x-6 border-b border-gray-200">
            <button
              className={`pb-3 ${activeTab2 === "present" ? "text-blue-600 border-b-2 border-blue-600 font-medium" : "text-gray-500"}`}
              onClick={() => handleTabChange("present")}
            >
              Present Directors (
              {cibilLoading ? (
                <Loader2 className="inline h-3.5 w-3.5 animate-spin align-[-2px]" />
              ) : (
                cibilRows.length
              )}
              )
            </button>
            <button
              className={`pb-3 ${activeTab2 === "past" ? "text-blue-600 border-b-2 border-blue-600 font-medium" : "text-gray-500"}`}
              onClick={() => handleTabChange("past")}
            >
              Past Directors ({ceasedDirectorsList.length})
            </button>
          </nav>
        </div>

        <TabsContent value="directors">
          {/* Directors Table */}
          <div className="rounded-lg border overflow-x-auto">
            <Table
              className={
                activeTab2 === "present"
                  ? "min-w-[880px] table-fixed"
                  : "table-fixed"
              }
            >
              <TableHeader className="bg-gray-50">
                <TableRow className="bg-gray-50">
                  {activeTab2 === "present" ? (
                    <>
                      <TableHead className="h-9 w-[220px] border-r border-gray-200 py-2 font-semibold">
                        Director Name
                      </TableHead>
                      <TableHead className="h-9 px-3 py-2 text-center font-semibold">DPD Accounts</TableHead>
                      <TableHead className="h-9 px-3 py-2 text-center font-semibold">Overdue</TableHead>
                      <TableHead className="h-9 px-3 py-2 text-center font-semibold">Enquiries (90D)</TableHead>
                      <TableHead className="h-9 px-3 py-2 text-center font-semibold">Max DPD</TableHead>
                      <TableHead className="h-9 px-3 py-2 text-center font-semibold">Fetched</TableHead>
                      <TableHead className="h-9 w-[140px] px-3 py-2 text-center font-semibold">Bureau Score</TableHead>
                    </>
                  ) : (
                    <>
                      <TableHead className="h-9 w-[36%] py-2 font-semibold">
                        Director Name
                      </TableHead>
                      <TableHead className="h-9 w-[44%] px-4 py-2 font-semibold">
                        Director Type
                      </TableHead>
                      <TableHead className="h-9 w-[20%] px-4 py-2 font-semibold">
                        Age
                      </TableHead>
                    </>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {activeTab2 === "present" ? (
                  cibilLoading ? (
                    Array.from({ length: 5 }).map((_, i) => (
                      <TableRow key={`cibil-skeleton-${i}`}>
                        <TableCell className="w-[220px] min-w-[200px] border-r border-gray-200 py-2.5 pr-4">
                          <div className="flex items-center gap-2.5">
                            <Skeleton className="h-8 w-8 shrink-0 rounded-full" />
                            <div className="min-w-0 flex-1 space-y-1.5">
                              <Skeleton className="h-4 w-32" />
                              <Skeleton className="h-3 w-20" />
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="px-3 py-2.5">
                          <Skeleton className="mx-auto h-5 w-10" />
                        </TableCell>
                        <TableCell className="px-3 py-2.5">
                          <Skeleton className="mx-auto h-4 w-14" />
                        </TableCell>
                        <TableCell className="px-3 py-2.5">
                          <Skeleton className="mx-auto h-5 w-10" />
                        </TableCell>
                        <TableCell className="px-3 py-2.5">
                          <Skeleton className="mx-auto h-4 w-10" />
                        </TableCell>
                        <TableCell className="px-3 py-2.5">
                          <Skeleton className="mx-auto h-3 w-16" />
                        </TableCell>
                        <TableCell className="px-3 py-2.5">
                          <Skeleton className="mx-auto h-7 w-[7.5rem]" />
                        </TableCell>
                      </TableRow>
                    ))
                  ) : cibilRows.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={7}
                        className="py-10 text-center text-sm text-muted-foreground"
                      >
                        No present directors found.
                      </TableCell>
                    </TableRow>
                  ) : (
                    cibilRows.map((row) => renderCibilRow(row))
                  )
                ) : ceasedDirectorsList.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={3}
                      className="py-10 text-center text-sm text-muted-foreground"
                    >
                      No past directors found.
                    </TableCell>
                  </TableRow>
                ) : (
                  ceasedDirectorsList.map((d) => renderDirectorRow(d))
                )}
              </TableBody>
            </Table>
          </div>

        </TabsContent>
      </Tabs>
      <FetchCibilDialog
        open={fetchFor != null}
        onOpenChange={(open) => {
          if (!open) setFetchFor(null);
        }}
        defaults={{
          name: fetchFor?.full_name,
          pan: fetchFor?.pan,
          mobile: fetchFor?.mobile,
          email: fetchFor?.email || undefined,
        }}
        customerId={customerId}
        directorsUuid={fetchFor?.director_uuid}
        onFetched={(row, list) => {
          if (list) {
            setCibilRows(list);
          } else {
            setCibilRows((prev) => {
              const key = row.director_uuid || row.pan?.trim().toUpperCase();
              const idx = prev.findIndex(
                (r) =>
                  (row.director_uuid && r.director_uuid === row.director_uuid) ||
                  (key && r.pan?.trim().toUpperCase() === key),
              );
              if (idx >= 0) {
                const next = [...prev];
                next[idx] = row;
                return next;
              }
              return [...prev, row];
            });
          }
          setFetchFor(null);
        }}
      />

      {/* Director Details Drawer — Apollo professional profile + MCA regulatory */}
      <Sheet open={isDrawerOpen} onOpenChange={setIsDrawerOpen}>
        <SheetContent className="w-full overflow-y-auto p-0 sm:max-w-3xl">
          <DirectorApolloPanel
            open={isDrawerOpen}
            director={selectedDirector}
            customerId={customerId}
            presentDirectorships={presentDirectorships}
            pastDirectorships={pastDirectorships}
            useTestData={false}
            onContactSaved={({ directorUuid, email, phone }) => {
              setCibilRows((prev) =>
                prev.map((row) =>
                  row.director_uuid === directorUuid
                    ? {
                        ...row,
                        mobile: phone || row.mobile,
                        email: email || row.email || null,
                      }
                    : row
                )
              );
            }}
          />
        </SheetContent>
      </Sheet>
    </div>
  );
}

export default DirectorDetails;
